import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { powerOfTwoValidator } from 'src/app/shared/validator/power-of-two.validator';
import { splitterValidator } from 'src/app/shared/validator/splitter.validator';
import { AcessosHorizontaisPorPortaPON, AttendanceSplitter, MitraNetworkParameters2, SecoesDeSplitter } from '../../state/network-parameters.model.generated';

@Component({
  selector: 'app-horizontal-balanced-form',
  templateUrl: './horizontal-balanced-form.component.html',
  styleUrls: ['./horizontal-balanced-form.component.scss']
})
export class HorizontalBalancedFormComponent implements OnInit {

  @Input()
  parameters: MitraNetworkParameters2;

  @Input()
  parentForm: FormGroup;

  sections = ['', null, null];
  sectionsError = false;

  readonly allowedSplitterValues = ALLOWED_SPLITTER_VALUES;

  form: FormGroup;


  constructor(
    private formBuilder: FormBuilder
  ) { }

  ngOnInit(): void {
    this.createForm();

    this.parameters.horizontal_settings.splitter_sections
      .forEach((section, i) => {
        this.sections[i] = `1x${section.splitter}`;
      });
  }

  sectionsChange(sections: string[]) {
    this.parameters.horizontal_settings.splitter_sections =
      sections
        .filter(s => s !== null && s !== '')
        .map(s => {
          return { splitter: this.splitterToNumber(s) };
        }) as SecoesDeSplitter;

    this.validateSections();
  }

  private createForm(): void {
    const { horizontal_access_per_pon, horizontal_settings } = this.parameters;
    const overbooking = horizontal_settings.overbooking ? horizontal_settings.overbooking * 100 : 0;

    let stringAttendanceSplitter: string;
    if (typeof (this.parameters.horizontal_settings.attendance_splitter) !== 'number') {
      stringAttendanceSplitter = '';
    } else {
      stringAttendanceSplitter = '1x' + horizontal_settings.attendance_splitter;
    }

    let connector: string;

    if (horizontal_settings.fiber_connections === 'With PTO') {
      connector = horizontal_settings.connector_drop_pto;
    } else {
      connector = horizontal_settings.connector_customer_drop_nopto;
    }

    this.form = this.formBuilder.group({
      overbooking: [overbooking, [Validators.required, Validators.min(0), Validators.max(100)]],
      accessPerPon: [horizontal_access_per_pon, [Validators.required, Validators.min(1), Validators.max(128), powerOfTwoValidator()]],
      attendanceSplitter: [stringAttendanceSplitter, [Validators.required, splitterValidator()]],
      max_drop_length: [horizontal_settings.max_drop_length, [Validators.required, Validators.min(0)]],
      connector_box_drop: [horizontal_settings.connector_box_drop, Validators.required],
      fiber_connections: [horizontal_settings.fiber_connections, Validators.required],
      connector: [connector, Validators.required]
    });

    this.form.valueChanges.subscribe(v => {
      this.validateSections();
      this.updateParameters();
    });

    this.parentForm.addControl('horizontal', this.form);
    this.parentForm.addControl('horizontalSections', this.formBuilder.control(true, Validators.requiredTrue));
  }

  private updateParameters() {
    const { value } = this.form;

    this.parameters.horizontal_access_per_pon = Number(value.accessPerPon) as AcessosHorizontaisPorPortaPON;
    this.parameters.horizontal_settings = {
      ...this.parameters.horizontal_settings,
      overbooking: value.overbooking ? value.overbooking / 100 : 0,
      attendance_splitter: this.splitterToNumber(value.attendanceSplitter) as AttendanceSplitter,
      max_drop_length: Number(value.max_drop_length),
      connector_box_drop: value.connector_box_drop,
      fiber_connections: value.fiber_connections
    };

    if (this.parameters.horizontal_settings.fiber_connections === 'With PTO') {
      this.parameters.horizontal_settings.connector_drop_pto = value.connector;
      this.parameters.horizontal_settings.connector_customer_drop_nopto = undefined;
    } else {
      this.parameters.horizontal_settings.connector_drop_pto = undefined;
      this.parameters.horizontal_settings.connector_customer_drop_nopto = value.connector;
    }
  }

  private validateSections(): void {
    const { splitter_sections } = this.parameters.horizontal_settings;

    let multiplication = 1;

    if (splitter_sections.length) {

      multiplication = splitter_sections.reduce((value, current) => {
        if (!ALLOWED_SPLITTER_VALUES.map(this.splitterToNumber).includes(current.splitter)) {
          return 0;
        }
        if (current.splitter) {
          return value * current.splitter;
        }
        return value;
      }, multiplication);
    }

    if (this.form.value.attendanceSplitter !== '' && this.splitterToNumber(this.form.value.attendanceSplitter) !== 0) {
      multiplication = multiplication * this.splitterToNumber(this.form.value.attendanceSplitter);
    }

    this.sectionsError = multiplication !== Number(this.form.value.accessPerPon);

    this.parentForm.get('horizontalSections').setValue(!this.sectionsError);
  }

  private splitterToNumber(splitter: string): number {
    const value = splitter.replace('1x', '');
    return Number(value);
  }
}

const ALLOWED_SPLITTER_VALUES = ['1x0', '1x2', '1x4', '1x8', '1x16', '1x32'];
